// ===============================================
// File: src/api/Landlords.js  (ADMIN fetch wrapper - fixed)
// ===============================================
const API_BASE = "https://landlord-portal-y3wv.onrender.com";

// NOTE: If your server path is actually /api/v1/users/landlords, fix this constant accordingly.
const LANDLORDS_BASE = `${API_BASE}/api/v1/users/api/v1/users/landlords`;

const ADMIN_API_PREFIX_ABS = `${API_BASE}/api/v1/admin/`;
const USERS_API_PREFIX_ABS = `${API_BASE}/api/v1/users/`;

/* ---------------- Utils ---------------- */
async function readErr(res) {
  const text = await res.text().catch(() => "");
  try {
    const json = JSON.parse(text);
    const nested =
      json?.error && typeof json.error === "object" ? json.error : null;
    return (
      nested?.detail ||
      nested?.message ||
      json?.detail ||
      json?.message ||
      text ||
      res.statusText
    );
  } catch {
    return text || res.statusText;
  }
}

function getStoredAuth() {
  const token = localStorage.getItem("access_token");
  const type = localStorage.getItem("token_type") || "Bearer";
  return { token, type };
}

/** One-time banner for admin sign-in. */
function setPostAuthNotice(type, message) {
  try {
    const payload = { type, message, ts: Date.now() };
    sessionStorage.setItem("post_auth_notice", JSON.stringify(payload));
  } catch {}
}

/** Build headers; don’t force Content-Type for FormData. */
function buildHeaders(body, extra = {}) {
  const { token, type } = getStoredAuth();
  const base = { Authorization: `${type} ${token}`, ...extra };
  if (body instanceof FormData) {
    const { ["Content-Type"]: _omit, ...rest } = base;
    return rest;
  }
  return { "Content-Type": "application/json", ...base };
}

/** Treat both /api/v1/admin/* and /api/v1/users/* as admin-area. */
function isAdminArea(url) {
  if (typeof url !== "string") return false;
  return (
    url.startsWith(ADMIN_API_PREFIX_ABS) || url.startsWith(USERS_API_PREFIX_ABS)
  );
}

// Prevent redirect storms
let _redirecting = false;
function redirectToSignInOnce(
  msg = "Your admin session expired. Please sign in again."
) {
  if (_redirecting) return;
  _redirecting = true;
  try {
    setPostAuthNotice("admin_session_expired", msg);
    localStorage.removeItem("access_token");
    localStorage.removeItem("token_type");
  } catch {}
  if (typeof window !== "undefined") {
    window.location.replace("/admin/sign-in");
  }
}

/**
 * Inject auth & redirect on auth failures for ANY admin-area endpoint.
 */
async function fetchAuthed(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: buildHeaders(options.body, options.headers || {}),
  });

  if (isAdminArea(url) && [401, 403, 419, 440].includes(res.status)) {
    let serverMsg = "Your admin session expired. Please sign in again.";
    try {
      const txt = await res.clone().text();
      const j = JSON.parse(txt);
      const nested = j?.error && typeof j.error === "object" ? j.error : null;
      serverMsg =
        nested?.detail ||
        nested?.message ||
        j?.detail ||
        j?.message ||
        serverMsg;
    } catch {}
    redirectToSignInOnce(serverMsg);
    throw new Error("Unauthorized");
  }

  return res;
}

/* ---------------- AUTH ---------------- */
export async function authLogin(email, password) {
  const res = await fetch(`${API_BASE}/api/v1/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });

  if (!res.ok) throw new Error((await readErr(res)) || "Invalid credentials");
  const data = await res.json();
  localStorage.setItem("access_token", data.access_token);
  localStorage.setItem("token_type", data.token_type || "Bearer");
  return data;
}

/* ---------------- ADMIN: TOKEN UPLOAD ---------------- */
export async function uploadAdminToken(file) {
  if (!file) throw new Error("No file provided");
  const form = new FormData();
  form.append("file", file, file.name || "token.json");

  const url = `${API_BASE}/api/v1/admin/tokens/upload`;
  const res = await fetchAuthed(url, { method: "POST", body: form });

  if (!res.ok) {
    const err = await readErr(res);
    throw new Error(err || "Failed to upload token");
  }
  return res.json().catch(() => ({}));
}

/* ---------------- LANDLORDS CRUD (admin area) ---------------- */
export async function listLandlords(page = 1, pageSize = 100) {
  const url = `${LANDLORDS_BASE}?page=${page}&page_size=${pageSize}`;
  const res = await fetchAuthed(url, { method: "GET" });
  if (!res.ok) {
    const err = await readErr(res);
    throw new Error(err || "Failed to fetch landlords");
  }
  return res.json();
}

export async function listAllLandlords(pageSize = 100, hardCapPages = 1000) {
  const all = [];
  let page = 1;

  while (page <= hardCapPages) {
    const data = await listLandlords(page, pageSize);
    const users = Array.isArray(data?.users) ? data.users : [];
    all.push(...users);

    // stop if server says this was the last page
    const total = typeof data?.total === "number" ? data.total : null;
    if (total != null) {
      const totalPages = Math.max(1, Math.ceil(total / pageSize));
      if (page >= totalPages) break;
    } else {
      // fallback: stop when a short page is returned
      if (users.length < pageSize) break;
    }

    page += 1;
  }

  return all;
}

export async function getLandlord(uid) {
  const res = await fetchAuthed(`${LANDLORDS_BASE}/${uid}`, { method: "GET" });
  if (!res.ok)
    throw new Error((await readErr(res)) || "Failed to fetch landlord");
  return res.json();
}

export async function createLandlord(data) {
  const res = await fetchAuthed(LANDLORDS_BASE, {
    method: "POST",
    body: JSON.stringify(data),
  });
  if (!res.ok)
    throw new Error((await readErr(res)) || "Failed to create landlord");
  return res.json();
}

export async function updateLandlord(uid, data) {
  const res = await fetchAuthed(`${LANDLORDS_BASE}/${uid}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  if (!res.ok)
    throw new Error((await readErr(res)) || "Failed to update landlord");
  return res.json();
}

export async function deleteLandlord(uid) {
  const res = await fetchAuthed(`${LANDLORDS_BASE}/${uid}`, {
    method: "DELETE",
  });
  if (!res.ok)
    throw new Error((await readErr(res)) || "Failed to delete landlord");
  return res.json();
}

export async function changePassword(uid, newPassword) {
  const res = await fetchAuthed(`${LANDLORDS_BASE}/${uid}/change-password`, {
    method: "POST",
    body: JSON.stringify({ new_password: newPassword }),
  });
  if (!res.ok)
    throw new Error((await readErr(res)) || "Failed to reset password");
  return res.json();
}
